import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ListChecks, ArrowUp, ArrowDown, LayoutList } from 'lucide-react';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { Button } from '@/shared/components/ui/button';
import { Badge } from '@/shared/components/ui/badge';
import { Card, CardContent } from '@/shared/components/ui/card';
import { Pagination } from '@/shared/components/custom/pagination/Pagination';
import { Toolbar } from '@/shared/components/custom/toolbar';
import { usePaginationFromUrl } from '@/shared/hooks/usePaginationFromUrl';
import { useMySummaryTasks } from '../hooks/useMySummaryTasks';
import { MySummaryTasksFilters } from './MySummaryTasksFilters';
import { MySummaryTasksTableRow } from './MySummaryTasksTableRow';
import { MySummaryTaskCard } from './MySummaryTaskCard';
import type { MyTasksParams } from '../types';

export function MySummaryTasksTable() {
  const { t } = useTranslation('personalSummary');

  const [filters, setFilters] = useState<Omit<MyTasksParams, 'page' | 'pageSize'>>({});
  const [sortAscending, setSortAscending] = useState(true);

  const { data: totalData } = useMySummaryTasks({ ...filters, page: 1, pageSize: 1 });
  const pagination = usePaginationFromUrl({ totalItems: totalData?.totalCount ?? 0 });

  const params: MyTasksParams = {
    ...filters,
    sortBy: 'dueDate',
    sortAscending,
    page: pagination.pageIndex + 1,
    pageSize: pagination.pageSize,
  };

  const { data, isLoading, isError, refetch } = useMySummaryTasks(params);
  const items = data?.items ?? [];

  // Project filter options are derived from whatever page/filter combination is
  // currently loaded — there is no cross-tenant "list my projects" endpoint to
  // build a complete list from (see plan 031). This means the dropdown reflects
  // only the current page and changes as the user navigates, rather than
  // accumulating history — a deliberately simple, zero-extra-state trade-off.
  // (An earlier attempt at cross-render accumulation via a mutable ref accumulator
  // failed this repo's react-hooks/refs lint rule, which hard-errors on reading a
  // ref's current value during render — see this plan's revision banner.)
  const projectOptions = Array.from(
    new Map(
      items
        .filter((task) => task.projectId && task.projectName)
        .map((task) => [task.projectId as string, task.projectName as string]),
    ),
  ).map(([id, name]) => ({ id, name }));

  const handleFiltersChange = (updated: Omit<MyTasksParams, 'page' | 'pageSize'>) => {
    setFilters(updated);
    pagination.first();
  };

  const handleDueDateSortToggle = () => {
    setSortAscending((current) => !current);
    pagination.first();
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent
          className="space-y-2 p-4"
          aria-hidden="true"
          data-testid="personal-summary-tasks-loading-skeleton"
        >
          <Skeleton className="h-8 w-32" />
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-14 w-full" />
          ))}
        </CardContent>
      </Card>
    );
  }

  if (isError) {
    return (
      <Card>
        <CardContent
          data-testid="personal-summary-tasks-error-message"
          className="rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-900 dark:bg-red-950"
        >
          <p className="text-sm text-red-700 dark:text-red-400">{t('tasks.error')}</p>
          <Button variant="outline" size="sm" className="mt-3" onClick={() => refetch()}>
            {t('common.retry')}
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card className="pb-0">
        <CardContent className="space-y-3 p-0">
          <Toolbar className="border-b px-4 pb-3">
            <div className="flex items-center gap-2">
              <LayoutList className="h-4 w-4 shrink-0 text-primary" />
              <h3 className="text-base font-semibold md:text-lg">{t('sections.tasks')}</h3>
              {(data?.totalCount ?? 0) > 0 && (
                <Badge className="bg-blue-50 text-xs text-blue-700 dark:bg-blue-950 dark:text-blue-300">
                  {data?.totalCount}
                </Badge>
              )}
            </div>
            <Toolbar.Actions>
              <MySummaryTasksFilters
                filters={filters}
                onChange={handleFiltersChange}
                projectOptions={projectOptions}
              />
            </Toolbar.Actions>
          </Toolbar>

          {!items.length ? (
            <div data-testid="personal-summary-tasks-empty-state" className="py-8 text-center">
              <ListChecks className="mx-auto mb-3 h-10 w-10 text-muted-foreground" />
              <p className="text-sm font-semibold">{t('tasks.empty.title')}</p>
              <p className="mt-1 text-xs text-muted-foreground">{t('tasks.empty.description')}</p>
            </div>
          ) : (
            <>
              <div className="space-y-2 md:hidden" data-testid="personal-summary-tasks-cards">
                {items.map((task) => (
                  <MySummaryTaskCard key={task.id} task={task} />
                ))}
              </div>

              <div
                className="hidden overflow-x-auto md:block"
                data-testid="personal-summary-tasks-table"
              >
                <table className="min-w-full table-auto">
                  <thead>
                    <tr className="border-b text-xs text-muted-foreground">
                      <th className="py-2 pl-4 pr-2 text-left font-medium">
                        {t('tasks.columns.task')}
                      </th>
                      <th className="px-2 py-2 text-left font-medium">
                        {t('tasks.columns.project')}
                      </th>
                      <th className="px-2 py-2 text-left font-medium">
                        {t('tasks.columns.status')}
                      </th>
                      <th className="px-2 py-2 text-left font-medium">
                        {t('tasks.columns.priority')}
                      </th>
                      <th className="px-2 py-2 text-left font-medium">
                        <button
                          type="button"
                          data-testid="personal-summary-tasks-sort-duedate"
                          className="flex items-center gap-1 font-semibold text-primary"
                          onClick={handleDueDateSortToggle}
                        >
                          {t('tasks.columns.dueDate')}
                          {sortAscending ? (
                            <ArrowUp className="size-3 shrink-0" />
                          ) : (
                            <ArrowDown className="size-3 shrink-0" />
                          )}
                        </button>
                      </th>
                      <th className="px-2 py-2 text-right font-medium">
                        {t('tasks.columns.estimated')}
                      </th>
                      <th className="px-2 py-2 text-right font-medium">
                        {t('tasks.columns.tracked')}
                      </th>
                      <th className="px-2 py-2 pr-4 text-right font-medium">
                        {t('tasks.columns.efficiency')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((task) => (
                      <MySummaryTasksTableRow key={task.id} task={task} />
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {(data?.totalCount ?? 0) > 0 && (
        <div data-testid="personal-summary-tasks-pagination">
          <Pagination {...pagination} />
        </div>
      )}
    </>
  );
}
